/**
 * The input form for a module (CLAUDE.md 14.1).
 *
 * **Deliberately not auto-generated from JSON Schema.** Section 14.1 rejects that
 * explicitly: structural input -- span tables, section editors, rebar layouts -- is
 * too domain-specific, and the auto-form trap produces a technically-correct form
 * nobody wants to use. JSON Schema is for *validation* and the API contract.
 *
 * So each module gets a hand-built form. This file holds the field definitions for the
 * demo module and the shared machinery every module form will reuse: labels through
 * i18n keys, units never translated, numeric fields that cannot be corrupted by a
 * stray scroll wheel, and errors associated with their control.
 *
 * When `rc_beam` lands in Phase B1 it gets its own form component beside this one and
 * the shared widgets move to `components/calc/` as 14.1 describes.
 */
import { useTranslation } from 'react-i18next'

import { Field, FieldLabel } from '@/components/ui/field'
import { NumberInput } from '@/components/ui/input'

interface FieldSpec {
  name: string
  /** i18n key. The label is prose and is translated (14.4). */
  labelKey: string
  /** Unit symbol. Notation, never translated. */
  unit: string
  step: number
  min?: number
  max?: number
}

/**
 * Field definitions per module.
 *
 * Keyed by module id so the page can render whichever module a position pins,
 * including an older version whose form differs (5.7).
 */
const MODULE_FIELDS: Record<string, FieldSpec[]> = {
  demo_beam: [
    { name: 'span', labelKey: 'field.demo.span', unit: 'm', step: 0.1, min: 0.1, max: 100 },
    { name: 'g_k', labelKey: 'field.demo.g_k', unit: 'kN/m', step: 0.5, min: 0, max: 1000 },
    { name: 'q_k', labelKey: 'field.demo.q_k', unit: 'kN/m', step: 0.5, min: 0, max: 1000 },
    { name: 'gamma_g', labelKey: 'field.demo.gamma_g', unit: '-', step: 0.05, min: 1, max: 2 },
    { name: 'gamma_q', labelKey: 'field.demo.gamma_q', unit: '-', step: 0.05, min: 1, max: 2 },
    { name: 'm_rd', labelKey: 'field.demo.m_rd', unit: 'kNm', step: 5, min: 0.1, max: 100000 },
  ],
}

/** Defaults, so a freshly created position has a calculable form rather than zeros. */
const MODULE_DEFAULTS: Record<string, Record<string, number>> = {
  demo_beam: { span: 6, g_k: 10, q_k: 5, gamma_g: 1.35, gamma_q: 1.5, m_rd: 120 },
}

export function defaultsFor(moduleId: string): Record<string, number> {
  return { ...(MODULE_DEFAULTS[moduleId] ?? {}) }
}

export function PositionInputs({
  moduleId,
  values,
  onChange,
}: {
  moduleId: string
  values: Record<string, number>
  onChange: (values: Record<string, number>) => void
}) {
  const { t } = useTranslation(['common', 'report'])
  const fields = MODULE_FIELDS[moduleId] ?? []

  if (fields.length === 0) {
    return <p className="text-sm text-text-muted">{t('common.empty')}</p>
  }

  return (
    <form className="space-y-4" onSubmit={(event) => event.preventDefault()}>
      <h2 className="text-base font-semibold">{t('nav.inputs')}</h2>

      {fields.map((field) => {
        const value = values[field.name] ?? MODULE_DEFAULTS[moduleId]?.[field.name] ?? 0
        const outOfRange =
          (field.min !== undefined && value < field.min) ||
          (field.max !== undefined && value > field.max)

        return (
          <Field
            key={field.name}
            error={
              outOfRange
                ? t('errors:error.calc.out_of_range', {
                    name: t(field.labelKey, { ns: 'report' }),
                    minimum: field.min,
                    maximum: field.max,
                  })
                : null
            }
          >
            {/* The unit sits in the label's hint slot: notation beside prose, and
                never translated (14.4). */}
            <FieldLabel hint={field.unit}>{t(field.labelKey, { ns: 'report' })}</FieldLabel>
            <NumberInput
              value={value}
              onValueChange={(next) => onChange({ ...values, [field.name]: next })}
              step={field.step}
              min={field.min}
              max={field.max}
            />
          </Field>
        )
      })}
    </form>
  )
}
